// client/src/components/admin/AdminLeagueEmailSettings.js
import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { toZonedTime, fromZonedTime } from 'date-fns-tz'
import PageLayout from '../common/layout/PageLayout'
import ApiLeagueEmailSettings from '../../api/ApiLeagueEmailSettings'
import ApiLeagues from '../../api/ApiLeagues'
import './AdminLeagueEmailSettings.css'

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'America/Anchorage', label: 'Alaska Time (US)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (US)' },
  { value: 'America/Phoenix', label: 'Arizona Time (US)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (UK)' },
  { value: 'Europe/Paris', label: 'Paris (France)' },
  { value: 'Europe/Berlin', label: 'Berlin (Germany)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (Japan)' },
  { value: 'Australia/Sydney', label: 'Sydney (Australia)' },
]

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * Calculate the next occurrence of a scheduled day and time in a given timezone
 * @param {number} scheduledDay - Day of week (0-6, Sunday=0)
 * @param {string} scheduledTime - Time in HH:MM format
 * @param {string} timezone - IANA timezone identifier
 * @returns {Date} Next run date in UTC
 */
export function calculateNextRun(scheduledDay, scheduledTime, timezone) {
  // Get current date in the target timezone
  const now = new Date()
  const nowInTimezone = toZonedTime(now, timezone)

  // Parse the scheduled time (HH:MM)
  const [hours, minutes] = scheduledTime.split(':').map(Number)

  // Start with today in the target timezone
  let nextRun = new Date(nowInTimezone)
  nextRun.setHours(hours, minutes, 0, 0)

  // Calculate days until next occurrence
  const currentDay = nowInTimezone.getDay()
  let daysUntilNext = scheduledDay - currentDay

  // If the scheduled day is today but time has passed, or day is in the past
  if (daysUntilNext < 0 || (daysUntilNext === 0 && nowInTimezone >= nextRun)) {
    daysUntilNext += 7
  }

  // Add the days
  nextRun.setDate(nextRun.getDate() + daysUntilNext)

  // Convert from timezone to UTC
  const nextRunUTC = fromZonedTime(nextRun, timezone)

  return nextRunUTC
}

function AdminLeagueEmailSettings() {
  const { leagueId } = useParams()
  const [league, setLeague] = useState(null)
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  // Form state
  const [draftScheduleEnabled, setDraftScheduleEnabled] = useState(false)
  const [draftScheduleDay, setDraftScheduleDay] = useState(1)
  const [draftScheduleTime, setDraftScheduleTime] = useState('09:00')
  const [draftScheduleTimezone, setDraftScheduleTimezone] =
    useState('America/New_York')

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Load league info
      const leagueData = await ApiLeagues.getLeague(parseInt(leagueId))
      setLeague(leagueData)

      // Load settings
      const settingsData = await ApiLeagueEmailSettings.getSettings(
        parseInt(leagueId),
      )
      setSettings(settingsData.settings)

      // Populate form
      setDraftScheduleEnabled(
        settingsData.settings.draft_schedule_enabled || false,
      )
      setDraftScheduleDay(settingsData.settings.draft_schedule_day || 1)

      // Convert HH:MM:SS to HH:MM for time input
      const timeString = settingsData.settings.draft_schedule_time || '09:00:00'
      const [hours, minutes] = timeString.split(':')
      setDraftScheduleTime(`${hours}:${minutes}`)

      setDraftScheduleTimezone(
        settingsData.settings.draft_schedule_timezone || 'America/New_York',
      )
    } catch (err) {
      console.error('Error loading data:', err)
      setError('Failed to load settings')
    } finally {
      setLoading(false)
    }
  }, [leagueId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSave = async (e) => {
    e.preventDefault()

    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)

      // Convert HH:MM to HH:MM:SS for backend
      const timeWithSeconds = `${draftScheduleTime}:00`

      const updates = {
        draft_schedule_enabled: draftScheduleEnabled,
        draft_schedule_day: draftScheduleDay,
        draft_schedule_time: timeWithSeconds,
        draft_schedule_timezone: draftScheduleTimezone,
      }

      const result = await ApiLeagueEmailSettings.updateSettings(
        parseInt(leagueId),
        updates,
      )
      setSettings(result.settings)
      setSuccessMessage('Settings saved successfully!')

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      console.error('Error saving settings:', err)
      setError('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const formatNextRun = () => {
    if (!draftScheduleEnabled) {
      return 'Scheduling is disabled'
    }

    const dayName =
      DAYS_OF_WEEK.find((d) => d.value === parseInt(draftScheduleDay))?.label ||
      'Unknown'
    const timezoneName =
      TIMEZONES.find((tz) => tz.value === draftScheduleTimezone)?.label ||
      draftScheduleTimezone

    // Calculate next run time
    const nextRunUTC = calculateNextRun(
      parseInt(draftScheduleDay),
      draftScheduleTime,
      draftScheduleTimezone,
    )

    // Format the next run date
    const month = MONTH_NAMES[nextRunUTC.getUTCMonth()]
    const day = nextRunUTC.getUTCDate()
    const year = nextRunUTC.getUTCFullYear()
    const hours = String(nextRunUTC.getUTCHours()).padStart(2, '0')
    const minutes = String(nextRunUTC.getUTCMinutes()).padStart(2, '0')

    const nextRunFormatted = `${month} ${day}, ${year} ${hours}:${minutes} UTC`
    const relativeTime = formatDistanceToNow(nextRunUTC, { addSuffix: true })

    return {
      schedule: `Every ${dayName} at ${draftScheduleTime} (${timezoneName})`,
      nextRun: `${nextRunFormatted} (${relativeTime})`,
    }
  }

  if (loading) {
    return (
      <PageLayout title="Email Draft Automation" noCard={true}>
        <p>Loading...</p>
      </PageLayout>
    )
  }

  if (error && !settings) {
    return (
      <PageLayout title="Email Draft Automation" noCard={true}>
        <div className="alert alert-danger">{error}</div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title={`Email Draft Automation - ${league?.name || 'League'}`}
      noCard={true}
    >
      <div className="league-email-settings">
        {error && <div className="alert alert-danger">{error}</div>}
        {successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}

        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Automated Email Draft Schedule</h5>
            <p className="text-muted mb-4">
              Configure automatic email draft generation for this league. Drafts
              will be generated on the schedule you specify, but you will still
              need to review and send them manually.
            </p>

            <form onSubmit={handleSave}>
              <div className="form-check mb-4">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="draftScheduleEnabled"
                  checked={draftScheduleEnabled}
                  onChange={(e) => setDraftScheduleEnabled(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="draftScheduleEnabled"
                >
                  <strong>
                    Enable automatic draft generation for this league
                  </strong>
                </label>
              </div>

              {draftScheduleEnabled && (
                <>
                  <div className="row mb-3">
                    <div className="col-12 col-md-4">
                      <label htmlFor="draftScheduleDay" className="form-label">
                        Day of Week
                      </label>
                      <select
                        id="draftScheduleDay"
                        className="form-select"
                        value={draftScheduleDay}
                        onChange={(e) =>
                          setDraftScheduleDay(parseInt(e.target.value))
                        }
                      >
                        {DAYS_OF_WEEK.map((day) => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-4">
                      <label htmlFor="draftScheduleTime" className="form-label">
                        Time
                      </label>
                      <input
                        type="time"
                        id="draftScheduleTime"
                        className="form-control"
                        value={draftScheduleTime}
                        onChange={(e) => setDraftScheduleTime(e.target.value)}
                      />
                    </div>

                    <div className="col-12 col-md-4">
                      <label
                        htmlFor="draftScheduleTimezone"
                        className="form-label"
                      >
                        Timezone
                      </label>
                      <select
                        id="draftScheduleTimezone"
                        className="form-select"
                        value={draftScheduleTimezone}
                        onChange={(e) =>
                          setDraftScheduleTimezone(e.target.value)
                        }
                      >
                        {TIMEZONES.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="alert alert-info">
                    <strong>Next scheduled run:</strong>
                    {(() => {
                      const nextRunInfo = formatNextRun()
                      return typeof nextRunInfo === 'string' ? (
                        <> {nextRunInfo}</>
                      ) : (
                        <>
                          {' '}
                          {nextRunInfo.schedule}
                          <br />
                          <strong>Next run:</strong> {nextRunInfo.nextRun}
                        </>
                      )
                    })()}
                    <br />
                    <small>
                      Drafts will be automatically generated on this schedule.
                      You will still need to review and send them manually from
                      the Email Outbox.
                    </small>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Schedule Settings'}
                  </button>
                </>
              )}

              {!draftScheduleEnabled && (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              )}
            </form>
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-body">
            <h5 className="card-title">How It Works</h5>
            <ul className="mb-0">
              <li>
                <strong>Automatic Generation:</strong> Email drafts are
                generated automatically on your configured schedule (e.g., every
                Monday at 9:00 AM).
              </li>
              <li>
                <strong>Manual Review:</strong> Generated drafts appear in the
                Email Outbox where you can review, edit, or cancel them before
                sending.
              </li>
              <li>
                <strong>Manual Send:</strong> You must manually send emails from
                the Email Outbox. Automation only generates drafts, it never
                sends them.
              </li>
              <li>
                <strong>Idempotency:</strong> The system prevents duplicate
                draft generation. Each league gets one set of drafts per week
                maximum.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}

export default AdminLeagueEmailSettings
